/**
 * AutoLivePointerService
 * Handles automatic live pointer functionality - auto-start, auto-stop based on video/screenshare events
 * Separated from main livePointerService for better code organization
 */

import reduxStore from "../../configureEnvStore";
import { DEVICE_TYPES, ENABLED } from "../../constants/Constants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { setAutoLivePointerInProgress, setLivePointerColors, setPointerOn } from "../../reducers/livePointer-reducer";
import { LogFirebase } from "../../services/firestore";
import { getUserDetailsById } from "../Twilio/Room/roomFunction";
import { AGENTDEFAULTCOLOR, STATUS } from "../../constants/LivePointerConstants";
import { sendDataTrack, sendMsgForPointer } from "../../shared/dataTrackFunction";
import { NOTIFICATION_TYPES } from "../../constants/DataTrackNotificationConstants";
import { isEmpty } from "lodash";
import { isSilentObserverAvailable } from "../../shared/function";
import { dualStreamEnabled } from "../../shared/utility";

// Use lazy require to avoid circular dependency with livePointerService
const getLivePointerService = () => require("./livePointerService").default;

/**
 * Wait for video element to be ready with actual dimensions (hybrid approach)
 * @param {number} elementTimeout - max time to wait for video element to appear in DOM
 * @param {number} maxWaitTime - max total time to wait for video to be fully ready (safety net)
 */
const waitForVideoReady = (elementTimeout = 10000, maxWaitTime = 30000) => {
    return new Promise((resolve, reject) => {
        const pollInterval = 100;
        let elapsedTime = 0;
        let timeoutId = null;
        let videoElement = null;

        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (videoElement) {
                videoElement.removeEventListener("loadeddata", onLoadedData);
                videoElement.removeEventListener("loadedmetadata", onLoadedData);
                videoElement.removeEventListener("canplay", onLoadedData);
            }
        };

        const isVideoReady = (video) => {
            return video && video.readyState >= 3 && video.videoWidth > 0 && video.videoHeight > 0;
        };

        const onLoadedData = () => {
            if (isVideoReady(videoElement)) {
                cleanup();
                LogFirebase("AutoLivePointer", "video_ready_detected", {
                    readyState: videoElement.readyState,
                    videoWidth: videoElement.videoWidth,
                    videoHeight: videoElement.videoHeight,
                });
                resolve(videoElement);
            }
        };

        const setupEventListeners = (video) => {
            videoElement = video;
            videoElement.addEventListener("loadeddata", onLoadedData);
            videoElement.addEventListener("loadedmetadata", onLoadedData);
            videoElement.addEventListener("canplay", onLoadedData);
        };

        const checkVideoReady = () => {
            const expandedVideoContainer = document.getElementById("expandedVideo");
            const video = expandedVideoContainer?.querySelector("video");

            if (isVideoReady(video)) {
                cleanup();
                resolve(video);
                return;
            }

            if (video && !videoElement) {
                setupEventListeners(video);
            }

            if (!video && elapsedTime >= elementTimeout) {
                cleanup();
                reject(new Error("Video element not found within timeout"));
                return;
            }

            if (elapsedTime >= maxWaitTime) {
                cleanup();
                LogFirebase("AutoLivePointer", "video_ready_timeout", {
                    hasElement: !!video,
                    readyState: video?.readyState,
                    videoWidth: video?.videoWidth,
                    videoHeight: video?.videoHeight,
                });
                reject(new Error("Video not ready within maximum wait time"));
                return;
            }

            elapsedTime += pollInterval;
            timeoutId = setTimeout(checkVideoReady, pollInterval);
        };

        checkVideoReady();
    });
};

class AutoLivePointerService {
    // ==================== FEATURE FLAG CHECKS ====================

    /**
     * Check if auto live pointer feature is enabled
     */
    isAutoLivePointerEnabled = () => {
        return PublicVariables.featureFlag.SHOWMELIVECURSORAUTOSTART === ENABLED;
    };

    /**
     * Check if main live pointer feature is enabled
     */
    isLivePointerFeatureEnabled = () => {
        return PublicVariables.featureFlag.SHOWMELIVECURSOR === ENABLED;
    };

    /**
     * Check if conflicting features (markup, OCR, measurement, AR markup) are in progress
     * Delegates to livePointerService to avoid duplication
     */
    isConflictingFeatureInProgress = () => {
        const livePointerService = getLivePointerService();
        return livePointerService.isConflictingFeatureInProgress();
    };

    /**
     * Check if auto live pointer should be skipped
     * Only checks essential conditions - NOT UI state which may change
     */
    shouldSkipAutoLivePointer = () => {
        if (!this.isAutoLivePointerEnabled()) {
            console.log("Auto LP skip: auto feature disabled");
            LogFirebase("AutoLivePointer", "skip_feature_disabled", {
                SHOWMELIVECURSORAUTOSTART: PublicVariables.featureFlag.SHOWMELIVECURSORAUTOSTART,
            });
            return true;
        }
        if (!this.isLivePointerFeatureEnabled()) {
            console.log("Auto LP skip: live pointer feature disabled");
            LogFirebase("AutoLivePointer", "skip_livepointer_disabled", {
                SHOWMELIVECURSOR: PublicVariables.featureFlag.SHOWMELIVECURSOR,
            });
            return true;
        }
        if (isSilentObserverAvailable()) {
            console.log("Auto LP skip: silent observer");
            LogFirebase("AutoLivePointer", "skip_silent_observer_available", true);
            return true;
        }
        return false;
    };

    /**
     * Check if streaming participant is a mobile app user (Android/iOS app)
     * Mobile apps control when pointer starts - we should NOT auto-start for them
     */
    isStreamingUserMobileApp = () => {
        const { streamingPartiIdentity } = reduxStore.getState().twilioEvents;

        if (streamingPartiIdentity === CALL_DATA.localIdentity) {
            return false;
        }

        const userDetails = getUserDetailsById(streamingPartiIdentity);
        const isMobileApp = userDetails?.device_type === DEVICE_TYPES.ANDROID_APP || userDetails?.device_type === DEVICE_TYPES.IOS_APP;

        if (isMobileApp) {
            console.log("Auto LP skip: streaming user is mobile app", userDetails?.device_type);
            LogFirebase("AutoLivePointer", "skip_mobile_app_streaming", {
                device_type: userDetails?.device_type,
                streamingPartiIdentity,
            });
        }
        return isMobileApp;
    };

    // ==================== MAIN AUTO-START FUNCTIONS ====================

    /**
     * Auto-start live pointer when video/screenshare starts
     * Differentiates between dual stream and single stream modes
     * @param {string} from - Source of the trigger (e.g., "local_video", "remote_video")
     * @param {object} track - Optional Twilio track object
     */
    autoStartLivePointer = (from, track = null) => {
        if (this.shouldSkipAutoLivePointer()) {
            console.log("Auto live pointer: Skipping - conditions not met");
            return;
        }

        const { expandView } = reduxStore.getState().commonReducer;

        if (dualStreamEnabled()) {
            // DUAL STREAM: Agent only, on expand view
            if (GLOBAL_MEETING_DATA.isAgent && expandView) {
                console.log("Auto live pointer (dual stream): Agent in expand view, triggering from", from);
                LogFirebase("AutoLivePointer", "dual_stream_start", {
                    from,
                    isAgent: true,
                    expandView: true,
                    hasTrack: !!track,
                });
                if (track && typeof track.on === "function") {
                    if (track.isStarted) {
                        waitForVideoReady()
                            .then(() => this.livePointerAutomaticOnOff("videoPublish"))
                            .catch((error) => LogFirebase("AutoLivePointer", "video_ready_failed", { from, error: error.message }));
                    } else {
                        const onTrackStarted = () => {
                            track.off("started", onTrackStarted);
                            waitForVideoReady()
                                .then(() => this.livePointerAutomaticOnOff("videoPublish"))
                                .catch((error) => LogFirebase("AutoLivePointer", "video_ready_failed", { from, error: error.message }));
                        };
                        track.on("started", onTrackStarted);
                    }
                } else {
                    waitForVideoReady()
                        .then(() => this.livePointerAutomaticOnOff("videoPublish"))
                        .catch((error) => LogFirebase("AutoLivePointer", "video_ready_failed", { from, error: error.message }));
                }
            } else {
                console.log("Auto live pointer (dual stream): Skipping - not agent or not expand view");
                LogFirebase("AutoLivePointer", "dual_stream_skip_conditions", {
                    isAgent: GLOBAL_MEETING_DATA.isAgent,
                    expandView,
                });
            }
        } else {
            // SINGLE STREAM: Use video loadeddata event pattern
            console.log("Auto live pointer (single stream): Triggering from", from);
            LogFirebase("AutoLivePointer", "single_stream_start", { from });
            this.autoPointerOnVideoAndSceenshare();
        }
    };

    /**
     * Auto-start live pointer after Markup/OCR exits
     * @param {string} from - Source identifier
     */
    autoStartLivePointerAfterMarkupOCR = (from) => {
        console.log("Auto live pointer after Markup/OCR: Triggering from", from);
        LogFirebase("AutoLivePointer", "restart_after_markup_ocr", { from });
        this.autoStartLivePointer(from);
    };

    // ==================== DUAL STREAM FUNCTIONS ====================

    /**
     * Automatic on/off for live pointer based on context (dual stream)
     * @param {string} from - "expand" | "videoPublish" | "collaps"
     */
    livePointerAutomaticOnOff = (from) => {
        if (!this.isAutoLivePointerEnabled()) return;

        const livePointerService = getLivePointerService();
        console.log("livePointerAutomaticOnOff called with:", from);

        if (from === "expand" || from === "videoPublish") {
            // Check if pointer is allowed (false for iOS screenshare)
            const { allowPointer } = reduxStore.getState().commonReducer;
            if (!allowPointer) {
                console.log("livePointerAutomaticOnOff: skipping - pointer not allowed (iOS screenshare)");
                LogFirebase("AutoLivePointer", "skip_pointer_not_allowed", { from, allowPointer });
                return;
            }

            if (this.isStreamingUserMobileApp() && !dualStreamEnabled()) {
                console.log("livePointerAutomaticOnOff: skipping - streaming user is mobile app in single stream");
                return;
            }

            const { autoLivePointerInProgress, livePointerInProgress, pointerOn } = reduxStore.getState().livePointer;
            if (autoLivePointerInProgress || livePointerInProgress) {
                console.log("livePointerAutomaticOnOff: pointer setup in progress, skipping");
                return;
            }

            if (from === "videoPublish" && pointerOn) {
                console.log("livePointerAutomaticOnOff: videoPublish - pointer already ON, no restart needed");
                return;
            }

            if (from === "expand" && livePointerService.checkLivePointerStatus()) {
                console.log("livePointerAutomaticOnOff: expand - turning off existing pointer to reset canvas");
                livePointerService.turnOffLivePointer();
            }

            reduxStore.dispatch(setAutoLivePointerInProgress(true));

            this.waitForVideoReadyThenExecute(() => {
                if (livePointerService.checkLivePointerStatus()) {
                    console.log("livePointerAutomaticOnOff: pointer already on after video ready, skipping");
                    reduxStore.dispatch(setAutoLivePointerInProgress(false));
                    return;
                }

                const { expandView } = reduxStore.getState().commonReducer;
                if (!expandView) {
                    console.log("livePointerAutomaticOnOff: expand view no longer active, skipping");
                    reduxStore.dispatch(setAutoLivePointerInProgress(false));
                    return;
                }

                console.log("livePointerAutomaticOnOff: Auto ON from:", from);
                LogFirebase("AutoLivePointer", "dual_stream_activated", { from });
                sendMsgForPointer(STATUS.ON, "0");

                setTimeout(() => {
                    const { expandView: stillExpandView } = reduxStore.getState().commonReducer;
                    const { screenSharingUsers, videoStreamingUsers } = reduxStore.getState().twilioEvents;
                    const isVideoAvailable = !isEmpty(videoStreamingUsers) || !isEmpty(screenSharingUsers);

                    if (!stillExpandView || !isVideoAvailable) {
                        console.log("livePointerAutomaticOnOff: conditions no longer met, skipping canvas creation");
                        LogFirebase("AutoLivePointer", "canvas_creation_skipped", {
                            stillExpandView,
                            isVideoAvailable,
                        });
                        reduxStore.dispatch(setAutoLivePointerInProgress(false));
                        return;
                    }

                    reduxStore.dispatch(setPointerOn(true));
                    reduxStore.dispatch(setAutoLivePointerInProgress(false));
                    LogFirebase("AutoLivePointer", "canvas_created_successfully", { from });
                    livePointerService.prepareCanvasForLivePointer("0");
                }, 500);
            });
        } else if (from === "collaps") {
            console.log("livePointerAutomaticOnOff: collapse - turning off pointer");
            LogFirebase("AutoLivePointer", "turning off pointer- dual_stream_collapse_stop", true);
            livePointerService.turnOffLivePointer();
            sendMsgForPointer(STATUS.OFF, "0");
        }
    };

    /**
     * Check and handle automatic live pointer on expand/collapse
     * @param {Array} trackArr - Array of video tracks
     */
    checkAutomaticLivePointerOnExpandCollap = (trackArr) => {
        if (!this.isAutoLivePointerEnabled() || !GLOBAL_MEETING_DATA.isAgent) return;

        // Check if pointer is allowed (false for iOS screenshare)
        const { allowPointer } = reduxStore.getState().commonReducer;
        if (!allowPointer) {
            console.log("checkAutomaticLivePointerOnExpandCollap: skipping - pointer not allowed (iOS screenshare)");
            LogFirebase("AutoLivePointer", "expand_skip_not_allowed", { allowPointer });
            return;
        }

        const { streamingPartiIdentity } = reduxStore.getState().twilioEvents;
        const userDetails = getUserDetailsById(streamingPartiIdentity);

        if (["1", "2", "3"].includes(userDetails?.device_type)) {
            this.livePointerAutomaticOnOff("expand");
        } else if (trackArr && trackArr.length > 0) {
            this.livePointerAutomaticOnOff("expand");
        } else if (streamingPartiIdentity === CALL_DATA.localIdentity) {
            const { startLocalVideo, isLocalUserScreenSharing } = reduxStore.getState().twilioEvents;
            if (startLocalVideo || isLocalUserScreenSharing) {
                this.livePointerAutomaticOnOff("expand");
            }
        }
    };

    /**
     * Check and start pointer on Markup/OCR exit for dual stream
     */
    checkPointerStartOnMarkupOcrExitForDualStream = () => {
        if (!this.isAutoLivePointerEnabled() || !GLOBAL_MEETING_DATA.isAgent) return;

        // Check if pointer is allowed (false for iOS screenshare)
        const { allowPointer } = reduxStore.getState().commonReducer;
        if (!allowPointer) {
            console.log("checkPointerStartOnMarkupOcrExitForDualStream: skipping - pointer not allowed (iOS screenshare)");
            LogFirebase("AutoLivePointer", "markup_ocr_exit_skip_not_allowed", { allowPointer });
            return;
        }

        const { streamingPartiIdentity } = reduxStore.getState().twilioEvents;
        const userDetails = getUserDetailsById(streamingPartiIdentity);

        if (!userDetails?.device_type) return;

        // allowPointer check above already handles iOS screenshare (blocked with allowPointer: false)
        // For all other cases (browsers, Android, iOS camera), trigger auto pointer
        this.livePointerAutomaticOnOff("videoPublish");
    };

    // ==================== SINGLE STREAM FUNCTIONS ====================

    /**
     * Activate pointer for single stream mode
     * Sends ON message to remote AND creates local canvas
     */
    autoPointerOnForSingleStream = () => {
        if (!this.isAutoLivePointerEnabled()) return;

        if (this.isStreamingUserMobileApp()) {
            console.log("autoPointerOnForSingleStream: skipping - streaming user is mobile app");
            return;
        }

        const livePointerService = getLivePointerService();
        if (livePointerService.checkLivePointerStatus()) {
            console.log("autoPointerOnForSingleStream: pointer already on, skipping");
            return;
        }

        const { autoLivePointerInProgress, livePointerInProgress } = reduxStore.getState().livePointer;
        if (autoLivePointerInProgress || livePointerInProgress) {
            console.log("autoPointerOnForSingleStream: pointer setup in progress, skipping");
            return;
        }

        console.log("autoPointerOnForSingleStream: activating pointer and sending ON message");

        const { startLocalVideo, isLocalUserScreenSharing } = reduxStore.getState().twilioEvents;

        LogFirebase("AutoLivePointer", "single_stream_activated", {
            startLocalVideo,
            isLocalUserScreenSharing,
        });

        reduxStore.dispatch(setAutoLivePointerInProgress(true));
        sendMsgForPointer(undefined, "0");

        setTimeout(() => {
            reduxStore.dispatch(setPointerOn(true));
            reduxStore.dispatch(setAutoLivePointerInProgress(false));
            LogFirebase("AutoLivePointer", "single_stream_canvas_created", true);
            livePointerService.prepareCanvasForLivePointer("0");
        }, 1500);
    };

    /**
     * Auto pointer on video and screenshare start (for single stream)
     */
    autoPointerOnVideoAndSceenshare = () => {
        if (!this.isAutoLivePointerEnabled() || dualStreamEnabled()) {
            console.log("Auto LP video/SS: skipped - auto disabled or dual stream");
            return;
        }

        console.log("Auto LP video/SS: starting");

        this.assignLivePointerColor(() => {
            this._waitForVideoElement()
                .then((videoElement) => {
                    console.log("Auto LP video/SS: video element found");
                    let callAutoLP = true;

                    const onLoadedData = () => {
                        console.log("Auto LP video/SS: loadedData event, readyState:", videoElement.readyState);
                        if (videoElement.readyState >= 3 && callAutoLP) {
                            setTimeout(() => {
                                const { screenSharingUsers, videoStreamingUsers, startLocalVideo, isLocalUserScreenSharing } =
                                    reduxStore.getState().twilioEvents;
                                const isAnyVideoAvailable = !isEmpty(videoStreamingUsers) || !isEmpty(screenSharingUsers);
                                const isLocalVideoActive = startLocalVideo || isLocalUserScreenSharing;

                                console.log("Auto LP video/SS: isAnyVideoAvailable:", isAnyVideoAvailable, "isLocalVideoActive:", isLocalVideoActive);

                                if ((isAnyVideoAvailable || isLocalVideoActive) && callAutoLP) {
                                    this.autoPointerOnForSingleStream();
                                    callAutoLP = false;
                                } else {
                                    console.log("Auto LP video/SS: Skipping - video no longer available");
                                }
                            }, 700);
                        }
                    };

                    if (videoElement.readyState >= 3) {
                        console.log("Auto LP video/SS: video already ready");
                        onLoadedData();
                    } else {
                        console.log("Auto LP video/SS: waiting for loadeddata event");
                        videoElement.addEventListener("loadeddata", onLoadedData, { once: true });
                    }
                })
                .catch((err) => {
                    console.log("Auto LP video/SS: video element not found", err.message);
                    LogFirebase("AutoLivePointer", "video_element_not_found", err.message);
                });
        });
    };

    // ==================== DATA TRACK FUNCTIONS ====================

    /**
     * Send data track for auto status with color codes
     * @param {string} from - "supportOldApp" to indicate old app support flag
     */
    sendDataTrackForStatusAuto = (from) => {
        // Check both: auto feature AND main live pointer feature must be enabled
        if (!this.isAutoLivePointerEnabled()) return;
        if (!this.isLivePointerFeatureEnabled()) return;

        const statusCode = "1";
        const oldAppSupport = from === "supportOldApp" ? "1" : "0";

        const { livePointerColors } = reduxStore.getState().livePointer;
        const msg = {
            notification_type: NOTIFICATION_TYPES.LIVEPOINTER.STATUS_START_STOP,
            identity: CALL_DATA.localIdentity,
            device_type: GLOBAL_MEETING_DATA.device_type,
            oldAppAutoLP: oldAppSupport,
            livepointer_data: JSON.stringify({
                USERCOLORS: livePointerColors,
                POINTERSTATUS: parseInt(statusCode),
            }),
        };
        sendDataTrack(msg);
    };

    /**
     * Check if auto live pointer should be sent to new participant
     */
    checkLivePointerForNewParticipant = () => {
        if (!this.isAutoLivePointerEnabled() || dualStreamEnabled()) return;

        if (CALL_DATA.myVideoON || CALL_DATA.myscreenShareON) {
            this.assignLivePointerColor(() => {
                this.sendDataTrackForStatusAuto("supportOldApp");
            });
        }
    };

    // ==================== UTILITY FUNCTIONS ====================

    /**
     * Wait for video element to appear in DOM
     * @param {number} timeout - Maximum time to wait in ms
     * @returns {Promise<HTMLVideoElement>}
     */
    _waitForVideoElement = (timeout = 10000) => {
        return new Promise((resolve, reject) => {
            const interval = 200;
            let elapsedTime = 0;

            const checkElement = () => {
                const videoElement = document.querySelector("#expandedVideo video");
                if (videoElement) {
                    resolve(videoElement);
                } else {
                    elapsedTime += interval;
                    if (elapsedTime >= timeout) {
                        reject(new Error("Video element not found within timeout"));
                    } else {
                        setTimeout(checkElement, interval);
                    }
                }
            };

            checkElement();
        });
    };

    /**
     * Wait for video element to be ready and then execute callback
     * Uses the Promise-based waitForVideoReady function
     * @param {Function} callback - Function to execute when video is ready
     * @param {number} maxWaitTime - Maximum time to wait in ms
     */
    waitForVideoReadyThenExecute = (callback, maxWaitTime = 10000) => {
        waitForVideoReady(maxWaitTime, maxWaitTime)
            .then(() => {
                console.log("waitForVideoReadyThenExecute: Video ready, executing callback");
                callback();
            })
            .catch(() => {
                console.log("waitForVideoReadyThenExecute: Timeout reached, executing callback anyway");
                callback();
            });
    };

    /**
     * Assign live pointer color and execute callback
     * @param {Function} callback - Callback to execute after color assignment
     */
    assignLivePointerColor = (callback) => {
        const { livePointerColors } = reduxStore.getState().livePointer;

        if (!livePointerColors[CALL_DATA.localIdentity]) {
            this.setLivePointerColors(CALL_DATA.localIdentity);
        }

        if (callback && typeof callback === "function") {
            callback();
        }
    };

    /**
     * Set live pointer colors for a participant
     * @param {string} identity - Participant identity
     */
    setLivePointerColors = (identity) => {
        const { livePointerColors } = reduxStore.getState().livePointer;
        if (!livePointerColors[identity]) {
            const updatedColors = { ...livePointerColors };
            const colorIndex = Object.keys(updatedColors).length % 10;
            const colors = ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#FFA500", "#800080", "#008000", "#000080"];
            updatedColors[identity] = colors[colorIndex] || AGENTDEFAULTCOLOR;
            reduxStore.dispatch(setLivePointerColors(updatedColors));
        }
    };
}

export default new AutoLivePointerService();
